import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getAllBlogPosts, addBlogPost, updateBlogPost, deleteBlogPost } from '../../utils/firestore';
import { getAllCategories } from '../../utils/firestore';
import { uploadImage } from '../../utils/imageUpload';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const ManageBlog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formType, setFormType] = useState('create'); // 'create' or 'edit'
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const { register, handleSubmit, formState: { errors }, reset, setValue, clearErrors, control } = useForm();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postsData, categoriesData] = await Promise.all([
          getAllBlogPosts(),
          getAllCategories()
        ]);
        setPosts(postsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Set form values when editing a post
  useEffect(() => {
    if (editingPost) {
      setValue('title', editingPost.title);
      setValue('excerpt', editingPost.excerpt);
      setValue('content', editingPost.content || '');
      setValue('category', editingPost.category);
      setValue('tags', editingPost.tags?.join(', ') || '');
    } else {
      reset();
    }
  }, [editingPost, reset, setValue]);

  const onSubmit = async (data) => {
    try {
      if (formType === 'create') {
        // Handle image upload
        let imageUrl = '';
        if (data.image[0]) {
          imageUrl = await uploadImage(data.image[0], 'blog-images');
        }
        
        // Create blog post
        const postData = {
          title: data.title,
          excerpt: data.excerpt,
          content: data.content, // ReactQuill content is already in HTML format
          category: data.category,
          tags: data.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
          image: imageUrl,
          createdAt: new Date()
        };
        
        await addBlogPost(postData);
        toast.success('Yazı başarıyla eklendi!');
      } else {
        // Handle image upload if new image provided
        let imageUrl = editingPost.image;
        if (data.image && data.image[0]) {
          imageUrl = await uploadImage(data.image[0], 'blog-images');
        }
        
        // Update blog post
        const postData = {
          title: data.title,
          excerpt: data.excerpt,
          content: data.content, // ReactQuill content is already in HTML format
          category: data.category,
          tags: data.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
          image: imageUrl
        };
        
        await updateBlogPost(editingPost.id, postData);
        toast.success('Yazı başarıyla güncellendi!');
      }
      
      // Refresh data and reset form
      const postsData = await getAllBlogPosts();
      setPosts(postsData);
      setShowForm(false);
      setEditingPost(null);
      reset();
    } catch (error) {
      console.error('Error saving blog post:', error);
      toast.error('Failed to save blog post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormType('edit');
    setShowForm(true);
    
    // Set form values after a brief delay to ensure the form is rendered
    setTimeout(() => {
      reset({
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        category: post.category,
        tags: post.tags?.join(', ') || ''
      });
    }, 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Blog postunu silmeye emin misiniz??')) {
      try {
        await deleteBlogPost(id);
        const postsData = await getAllBlogPosts();
        setPosts(postsData);
        toast.success('Blog postu silindi.');
      } catch (error) {
        console.error('Error deleting blog post:', error);
        toast.error('Failed to delete blog post');
      }
    }
  };

  const handleShareToInstagram = (post) => {
    setSelectedPost(post);
    setShareUrl(`${window.location.origin}/blog/${post.id}`);
    setIsShareModalOpen(true);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    setSelectedPost(null);
    setShareUrl('');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
  };

  const downloadPostImage = () => {
    // Create a simple image with post title for sharing
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions (Instagram story size)
    canvas.width = 1080;
    canvas.height = 1920;
    
    // Fill background
    ctx.fillStyle = '#4A9D7D'; // Your brand color
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add subtle pattern or texture to background
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    for (let i = 0; i < canvas.width; i += 20) {
      for (let j = 0; j < canvas.height; j += 20) {
        if ((i + j) % 40 === 0) {
          ctx.beginPath();
          ctx.arc(i, j, 2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    
    // Add blog content excerpt as background text (subtle)
    if (selectedPost?.content) {
      // Extract text content from HTML (simple approach)
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = selectedPost.content;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      
      // Split into words and take first 50 words for background
      const words = plainText.split(' ').slice(0, 50);
      if (words.length > 0) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.05)'; // Very subtle
        ctx.font = '16px Arial';
        ctx.textAlign = 'left';
        
        let yPos = 100;
        let line = '';
        
        for (let i = 0; i < words.length; i++) {
          const testLine = line + words[i] + ' ';
          const metrics = ctx.measureText(testLine);
          
          if (metrics.width > canvas.width - 100 && i > 0) {
            ctx.fillText(line, 50, yPos);
            line = words[i] + ' ';
            yPos += 25;
          } else {
            line = testLine;
          }
          
          // Limit background text to prevent overcrowding
          if (yPos > canvas.height - 200) break;
        }
        
        // Draw remaining line
        if (line && yPos <= canvas.height - 200) {
          ctx.fillText(line, 50, yPos);
        }
      }
    }
    
    // Add main title
    ctx.fillStyle = 'white';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    
    // Split title into lines
    const title = selectedPost?.title || '';
    const titleWords = title.split(' ');
    const titleLines = [];
    let currentLine = '';
    
    titleWords.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > canvas.width - 100) {
        titleLines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });
    
    titleLines.push(currentLine);
    
    // Draw title lines
    const startY = canvas.height / 2 - (titleLines.length * 30);
    titleLines.forEach((line, index) => {
      ctx.fillText(line, canvas.width / 2, startY + (index * 60));
    });
    
    // Add website URL
    ctx.font = '24px Arial';
    ctx.fillText('dietitiannihalkamis.com', canvas.width / 2, canvas.height - 100);
    
    // Convert to blob and download
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blog-${selectedPost?.id || 'post'}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nihal-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Blog Yazılarını Yönet</h1>
        <button
          onClick={() => {
            setFormType('create');
            setEditingPost(null);
            setShowForm(true);
          }}
          className="btn btn-primary"
        >
          Yeni Yazı Ekle
        </button>
      </div>

      {showForm ? (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            {formType === 'create' ? 'Yeni Blog Yazısı Oluştur' : 'Blog Yazısını Düzenle'}
          </h2>
          
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Başlık *
                </label>
                <input
                  id="title"
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('title', { required: 'Başlık zorunlu' })}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Kategori *
                </label>
                <select
                  id="category"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary ${
                    errors.category ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('category', { required: 'Kategori zorunlu' })}
                >
                  <option value="">Kategori Seç</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
                )}
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
                Kısa Özet *
              </label>
              <textarea
                id="excerpt"
                rows="3"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary ${
                  errors.excerpt ? 'border-red-500' : 'border-gray-300'
                }`}
                {...register('excerpt', { required: 'Kısa özet zorunlu' })}
              ></textarea>
              {errors.excerpt && (
                <p className="mt-1 text-sm text-red-600">{errors.excerpt.message}</p>
              )}
            </div>
            
            <div className="mb-6">
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                İçerik *
              </label>
              <Controller
                name="content"
                control={control}
                rules={{ required: 'İçerik zorunlu' }}
                render={({ field }) => (
                  <div className="bg-white rounded-md border border-gray-300">
                    <ReactQuill
                      theme="snow"
                      value={field.value || ''}
                      onChange={field.onChange}
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          [{ 'color': [] }, { 'background': [] }],
                          ['link', 'image'],
                          ['clean']
                        ],
                      }}
                      className="bg-white"
                    />
                  </div>
                )}
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
              )}
            </div>
            
            <div className="mb-6">
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                Etiket (virgülle ayrılmış)
              </label>
              <input
                id="tags"
                type="text"
                placeholder="✅ örn., beslenme, diyet, sağlık"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                {...register('tags')}
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
                Yazı Görseli
              </label>
              <input
                id="image"
                type="file"
                accept="image/*"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                {...register('image')}
              />
              {editingPost?.image && !register('image').value && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">Varolan Görsel:</p>
                  <img 
                    src={editingPost.image} 
                    alt="Current" 
                    className="w-32 h-32 object-cover mt-2 rounded"
                  />
                </div>
              )}
            </div>
            
            <div className="flex space-x-4">
              <button
                type="submit"
                className="btn btn-primary"
              >
                {formType === 'create' ? 'Yazıyı Ekle' : 'Yazıyı Güncelle'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingPost(null);
                }}
                className="btn btn-secondary"
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Başlık
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kategori
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tarih
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Eylem
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {posts.map((post) => (
                <tr key={post.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{post.title}</div>
                    <div className="text-sm text-gray-500 line-clamp-2">{post.excerpt}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{post.category}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {post.createdAt?.toDate ? new Date(post.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(post)}
                        className="text-blue-600 hover:text-indigo-900"
                      >
                        Düzenle
                      </button>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {posts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Blog yazısı bulunamadı. İlk blog yazını oluştur!</p>
          </div>
        )}
      </div>
      
      {/* Instagram Share Modal */}
      {isShareModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-nihal-dark">Share to Instagram</h3>
                <button 
                  onClick={closeShareModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold">{selectedPost?.title}</h3>
                </div>
                
                <div className="bg-gray-100 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-600 mb-2">Share this link to your Instagram story:</p>
                  <div className="flex items-center">
                    <input 
                      type="text" 
                      value={shareUrl} 
                      readOnly 
                      className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none"
                    />
                    <button 
                      onClick={copyToClipboard}
                      className="bg-nihal-primary text-white px-4 py-2 rounded-r-md hover:bg-nihal-dark"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-2">Or download a shareable image:</p>
                  <button 
                    onClick={downloadPostImage}
                    className="w-full bg-nihal-primary text-white px-4 py-2 rounded-md hover:bg-nihal-dark flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                    </svg>
                    Download Image for Instagram
                  </button>
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg text-left">
                  <h4 className="font-bold mb-2">How to share on Instagram:</h4>
                  <ol className="text-sm text-gray-700 list-decimal pl-5 space-y-1">
                    <li>Copy the link above</li>
                    <li>Open Instagram app</li>
                    <li>Create a new story</li>
                    <li>Add text or stickers</li>
                    <li>Tap the paperclip icon</li>
                    <li>Paste the link</li>
                    <li>Share your story!</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBlog;